import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, Globe, MapPin, Plus, LogOut, User, 
  Loader2, Download, ArrowRight 
} from "lucide-react";
import ReferralCard from "../components/dashboard/ReferralCard";
import BookmarksSection from "../components/dashboard/BookmarksSection";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, reportsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/reports`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setStats(statsRes.data);
        setReports(reportsRes.data.filter(r => r.status === "completed"));
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 
            className="text-2xl font-bold text-primary cursor-pointer" 
            onClick={() => navigate("/")}
            data-testid="logo"
          >
            ExpatMatch
          </h1>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline"
              onClick={() => navigate("/questionnaire")}
              className="gap-2"
              data-testid="new-search-btn"
            >
              <Plus className="h-4 w-4" />
              New Search
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="gap-2 text-muted-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-primary mb-2" data-testid="welcome-title">
            Welcome back, {user?.name?.split(" ")[0]}!
          </h2>
          <p className="text-muted-foreground">
            Manage your relocation journey from your dashboard
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-card rounded-xl border border-primary/10 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-mono text-3xl font-bold text-primary">
                  {stats?.reports_purchased || 0}
                </p>
                <p className="text-sm text-muted-foreground">Reports Purchased</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-xl border border-primary/10 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Globe className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="font-mono text-3xl font-bold text-secondary">
                  {stats?.questionnaires_completed || 0}
                </p>
                <p className="text-sm text-muted-foreground">Searches Completed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-xl border border-primary/10 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <User className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-lg font-semibold text-primary truncate">
                  {user?.email}
                </p>
                <p className="text-sm text-muted-foreground">Your Account</p>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Program */}
        <ReferralCard token={token} />

        {/* Saved Countries */}
        <BookmarksSection token={token} />

        {/* Quick Recommendations */}
        {stats?.latest_recommendations?.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-primary">Your Top Matches</h3>
              <Button
                variant="ghost"
                onClick={() => navigate("/results")}
                className="gap-2"
                data-testid="view-all-matches-btn"
              >
                View All
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {stats.latest_recommendations.map((rec, i) => (
                <div
                  key={rec.country_name ? rec.country_name : "match-" + i}
                  className="bg-card rounded-xl border border-primary/10 p-4 card-hover cursor-pointer"
                  onClick={() => navigate("/results")}
                  data-testid={`quick-match-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-secondary" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-primary truncate">{rec.country_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {rec.match_score}% match
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Purchased Reports */}
        <div>
          <h3 className="text-2xl font-bold text-primary mb-6">Your Reports</h3>
          
          {reports.length === 0 ? (
            <div className="bg-card rounded-xl border border-primary/10 p-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h4 className="text-xl font-semibold text-primary mb-2">No Reports Yet</h4>
              <p className="text-muted-foreground mb-6">
                Complete a questionnaire and purchase your first relocation report
              </p>
              <Button
                onClick={() => navigate("/questionnaire")}
                className="btn-animated bg-primary"
                data-testid="start-search-btn"
              >
                Start Your Search
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-card rounded-xl border border-primary/10 p-6 card-hover"
                  data-testid={`report-${report.country_name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-xl font-semibold text-primary mb-1">
                        {report.country_name}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Generated {new Date(report.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/report/${encodeURIComponent(report.country_name)}`)}
                        className="gap-1"
                        data-testid={`view-${report.country_name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigate(`/report/${encodeURIComponent(report.country_name)}`);
                        }}
                        className="gap-1"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-primary/10 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-muted-foreground text-sm">
          © 2024 ExpatMatch. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default DashboardPage;
